/**
 * #206 live work in progress: who is on a case right now ("Live: Travis since 4:51 PM"), "I'm on it now"
 * (`go_live` card) and "Done for now" (`end_live` card). Every write is a frozen card confirmed through the generic
 * `POST /cards/{id}/confirm`. Owner: #205/#206 (web half).
 */
import { queryOptions } from '@tanstack/react-query'
import { formatWhen } from '../../lib/display-time'
import { apiRequest, type CardDto } from '../board/api'

export { describeError as errorText } from '../../lib/api/client'

export type LiveSource = 'manual' | 'assist' | 'wo_checkin' | 'chat' | 'phone'

/** openapi: LiveWorkOut */
export interface LiveWorkDto {
  id: string
  case_id: string
  case_number: string
  user_id?: string | null
  who: string
  source: LiveSource
  source_ref?: string | null
  started_at: string
  mine?: boolean
}

/** openapi: LiveSuggestionOut. #1014: a signal offering Work it now; it never starts a timer. */
export interface LiveSuggestionDto {
  source: LiveSource
  reason: string
  at: string
}

/** openapi: CaseLiveOut */
export interface CaseLiveDto {
  live: LiveWorkDto[]
  working_status: string
  waiting_status: string
  resolved_status: string
  desk_status?: string | null
  suggestion?: LiveSuggestionDto | null
}

/** openapi: MyLiveSessionOut. #1014: the person's one open timer, owned by the server. */
export interface MyLiveSessionDto {
  id: string
  case_id: string
  case_number: string
  desk_ticket_id?: string | null
  ticket_number?: string | null
  source: LiveSource
  started_at: string
  notes?: string
  notes_updated_at?: string | null
}

/** openapi: MyLiveOut */
export interface MyLiveDto {
  session?: MyLiveSessionDto | null
  server_now: string
}

export type EndOutcome = 'waiting' | 'resolved' | 'keep'

/** CardOut as these screens read it (case_id/editable are on the contract; CardDto predates them). */
export interface RemoteWorkCard extends CardDto {
  case_id?: string | null
  editable?: boolean
}

type Requester = <T>(path: string, init?: RequestInit) => Promise<T>

let requester: Requester = apiRequest

/**
 * src/test/setup.ts installs a quiet requester (nothing live, Assist off) so every other screen's tests render these
 * controls without a network; a test passes `null` to exercise the real fetch path with a stubbed `fetch`.
 */
export function installRemoteWorkTestRequester(r: Requester | null): void {
  if (import.meta.env.MODE !== 'test') throw new Error('Remote-work test requester is test-only')
  requester = r ?? apiRequest
}

export const request: Requester = (path, init) => requester(path, init)

const seg = (v: string) => encodeURIComponent(v)

export const liveApi = {
  all: () => request<LiveWorkDto[]>('/api/v1/live'),
  forCase: (caseId: string) => request<CaseLiveDto>(`/api/v1/cases/${seg(caseId)}/live`),
  /** #1014 Work it now. `switch` ends your timer on another case (409 `live_elsewhere` without it). */
  proposeGoLive: (caseId: string, switchTimer = false) =>
    request<RemoteWorkCard>(`/api/v1/cases/${seg(caseId)}/live`, {
      method: 'POST',
      body: JSON.stringify({ switch: switchTimer }),
    }),
  me: () => request<MyLiveDto>('/api/v1/live/me'),
  saveNotes: (liveId: string, notes: string) =>
    request<{ id: string; notes_updated_at: string }>(`/api/v1/live/${seg(liveId)}/notes`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    }),
  proposeEnd: (caseId: string, outcome: EndOutcome) =>
    request<RemoteWorkCard>(`/api/v1/cases/${seg(caseId)}/live/end`, { method: 'POST', body: JSON.stringify({ outcome }) }),
  /** The proposed cards awaiting a decision; the case strip filters them to its own case. */
  proposals: () => request<{ cards: RemoteWorkCard[] }>('/api/v1/proposals'),
  confirm: (cardId: string, editedPayload?: Record<string, unknown> | null) =>
    request<RemoteWorkCard>(`/api/v1/cards/${seg(cardId)}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ edited_payload: editedPayload ?? null }),
    }),
  dismiss: (cardId: string, reason?: string | null) =>
    request<RemoteWorkCard>(`/api/v1/cards/${seg(cardId)}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? null }),
    }),
}

export const remoteWorkKeys = {
  all: ['remote-work'] as const,
  live: ['remote-work', 'live'] as const,
  me: ['remote-work', 'live', 'me'] as const,
  case: (caseId: string) => ['remote-work', 'case', caseId] as const,
  caseLive: (caseId: string) => ['remote-work', 'case', caseId, 'live'] as const,
  sessions: (caseId: string) => ['remote-work', 'case', caseId, 'sessions'] as const,
  cards: ['remote-work', 'proposals'] as const,
  status: ['remote-work', 'assist-status'] as const,
}

/** Every open live marker, once for the whole queue (joined to rows on case_id). */
export const liveAllQuery = () =>
  queryOptions({ queryKey: remoteWorkKeys.live, queryFn: liveApi.all, staleTime: 30_000, refetchInterval: 60_000, retry: false })

/** #1014: my open timer. Rehydrates on every load and refetches on focus, so refresh, navigation and other tabs
 * all show the same server-owned session. */
export const myLiveQuery = () =>
  queryOptions({ queryKey: remoteWorkKeys.me, queryFn: liveApi.me, staleTime: 15_000, refetchInterval: 60_000, retry: false })

export const caseLiveQuery = (caseId: string) =>
  queryOptions({ queryKey: remoteWorkKeys.caseLive(caseId), queryFn: () => liveApi.forCase(caseId), staleTime: 15_000, retry: false })

export const proposalsQuery = () =>
  queryOptions({ queryKey: remoteWorkKeys.cards, queryFn: liveApi.proposals, staleTime: 15_000, retry: false })

/** "4:51 PM", Toronto time (the site and the team's clock); '' when unreadable. */
export function torontoTime(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : formatWhen(iso, { style: 'time' })
}

/** "Live: Travis since 4:51 PM" (several people: "Live: Travis, Sam since 4:51 PM", the earliest start). */
export function liveLabel(rows: Pick<LiveWorkDto, 'who' | 'started_at'>[]): string {
  if (!rows.length) return ''
  const sorted = [...rows].sort((a, b) => a.started_at.localeCompare(b.started_at))
  const names = [...new Set(sorted.map((r) => r.who))].join(', ')
  const since = torontoTime(sorted[0]?.started_at)
  return since ? `Live: ${names} since ${since}` : `Live: ${names}`
}

/** "1:05:09" / "4:07": elapsed time for the timer. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const sec = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`
}
